/**
 * Normalization utilities for API responses
 * Converts nested DTO structures into flat, normalized store entities
 *
 * ✅ NOW USES DTOs - Much cleaner and type-safe!
 * - DTOs already validated data at API boundary
 * - DTOs already resolved names (no more fallback chains)
 * - Just transform DTOs to store entities
 */

import { HomeDTO, RoomDTO, DeviceDTO, GatewayDTO } from '@/models/dtos';
import {
  HomeEntity,
  RoomEntity,
  DeviceEntity,
  GatewayEntity,
  NormalizedData,
  Relationships,
  createEmptyRelationships,
} from './types';

/**
 * Normalize homes from validated DTOs
 * Input: Array of validated HomeDTOs
 * Output: Normalized entities and relationships
 */
export function normalizeHomeDTOs(homeDTOs: HomeDTO[]): NormalizedData {
  const homes: Record<string, HomeEntity> = {};
  const rooms: Record<string, RoomEntity> = {};
  const devices: Record<string, DeviceEntity> = {};
  const gateways: Record<string, GatewayEntity> = {};
  const relationships = createEmptyRelationships();

  console.log('[Normalizer] Processing HomeDTOs:', homeDTOs);

  homeDTOs.forEach(homeDTO => {
    const homeUid = homeDTO.uid;

    console.log('[Normalizer] Processing home:', homeDTO.name, 'Rooms:', homeDTO.rooms);

    // ✅ Store home entity - DTO already validated everything
    homes[homeUid] = {
      uid: homeDTO.uid,
      name: homeDTO.name,
      userUid: homeDTO.userUid,
      deviceUids: homeDTO.deviceUids,
      gatewayUids: homeDTO.gatewayUids,
    };

    // Initialize home relationships
    relationships.homeToRooms[homeUid] = [];
    relationships.homeToDevices[homeUid] = homeDTO.deviceUids;
    relationships.homeToGateways[homeUid] = homeDTO.gatewayUids;

    // Map gateway to home relationships
    homeDTO.gatewayUids.forEach(gatewayUid => {
      relationships.gatewayToHome[gatewayUid] = homeUid;
    });

    // Map device to home relationships
    homeDTO.deviceUids.forEach(deviceId => {
      relationships.deviceToHome[deviceId] = homeUid;
    });

    // ✅ Process rooms - already validated by RoomDTO
    homeDTO.rooms.forEach(roomDTO => {
      const roomUid = roomDTO.uid;

      // Store room entity
      rooms[roomUid] = {
        uid: roomDTO.uid,
        name: roomDTO.name,
        homeUid: homeUid,
        devices: roomDTO.devices.map(d => d.toStoreEntity()),
        gateways: roomDTO.gateways,
      };

      // Map room to home
      relationships.roomToHome[roomUid] = homeUid;
      relationships.homeToRooms[homeUid].push(roomUid);

      // Initialize room to devices relationship
      relationships.roomToDevices[roomUid] = [];

      // ✅ Process devices - already validated by DeviceDTO
      roomDTO.devices.forEach(deviceDTO => {
        const deviceId = deviceDTO.id;

        // Store device entity - DTO already resolved name!
        devices[deviceId] = {
          id: deviceDTO.id,
          name: deviceDTO.name, // ✅ DTO already picked best name
          deviceAppName: deviceDTO.deviceAppName,
          gatewayId: deviceDTO.gatewayId,
          deviceInfo: deviceDTO.deviceInfo,
        };

        // Map device to room
        relationships.deviceToRoom[deviceId] = roomUid;
        relationships.roomToDevices[roomUid].push(deviceId);

        // Extract gateway relationship from device
        if (deviceDTO.gatewayId) {
          relationships.deviceToGateway[deviceId] = deviceDTO.gatewayId;
        }
      });

      // Process gateways in room (basic info)
      roomDTO.gateways.forEach((gateway: any) => {
        const gatewayUid = gateway.uid || gateway.id;
        if (!gatewayUid) return;

        // Store basic gateway info (will be enriched by registries data)
        if (!gateways[gatewayUid]) {
          gateways[gatewayUid] = {
            uid: gatewayUid,
            groupId: gatewayUid,
            name: gateway.gatewayAppName || gateway.name || gatewayUid,
            gatewayAppName: gateway.gatewayAppName || null,
            deviceIds: [],
          };
        }
      });
    });
  });

  return { homes, rooms, devices, gateways, relationships };
}

/**
 * Normalize registries from validated DTOs
 * Input: Array of validated GatewayDTOs
 * Output: Normalized entities and relationships
 */
export function normalizeRegistryDTOs(gatewayDTOs: GatewayDTO[]): Partial<NormalizedData> {
  const devices: Record<string, DeviceEntity> = {};
  const gateways: Record<string, GatewayEntity> = {};
  const relationships: Partial<Relationships> = {
    deviceToGateway: {},
    gatewayToDevices: {},
  };

  console.log('[Normalizer] Processing GatewayDTOs:', gatewayDTOs);

  gatewayDTOs.forEach(gatewayDTO => {
    const gatewayUid = gatewayDTO.uid;

    console.log('[Normalizer] Gateway UID:', gatewayUid);
    console.log('[Normalizer] Gateway name:', gatewayDTO.name);

    // ✅ Store gateway entity - DTO already resolved name!
    gateways[gatewayUid] = {
      uid: gatewayDTO.uid,
      groupId: gatewayDTO.groupId,
      name: gatewayDTO.name, // ✅ DTO already picked best name
      gatewayAppName: gatewayDTO.gatewayAppName,
      deviceIds: gatewayDTO.deviceIds,
      status: gatewayDTO.status,
    };

    console.log('[Normalizer] Gateway stored with name:', gatewayDTO.name);

    // Initialize gateway to devices relationship
    relationships.gatewayToDevices![gatewayUid] = [];

    // ✅ Process devices - already validated
    // Note: Registries API returns raw device objects, not DTOs yet
    // This will be improved when we create GatewayService
    gatewayDTO.deviceIds.forEach(deviceId => {
      relationships.gatewayToDevices![gatewayUid].push(deviceId);
    });
  });

  return { devices, gateways, relationships: relationships as Relationships };
}

/**
 * Merge homes and registries normalized data
 * Priority: registries data (more detailed) > homes data
 */
export function mergeNormalizedData(
  homesNormalized: NormalizedData,
  registriesNormalized: Partial<NormalizedData>
): NormalizedData {
  // Merge devices: registries have more detailed info
  const mergedDevices = { ...homesNormalized.devices };
  Object.keys(registriesNormalized.devices || {}).forEach(deviceId => {
    const registryDevice = registriesNormalized.devices![deviceId];
    const homeDevice = mergedDevices[deviceId] || {};

    // Merge device data, prioritizing registry data
    mergedDevices[deviceId] = {
      ...homeDevice,
      ...registryDevice,
      // ✅ DTOs already resolved names, just use them
      name: registryDevice.deviceAppName || registryDevice.name || homeDevice.name || deviceId,
      deviceAppName: registryDevice.deviceAppName || homeDevice.deviceAppName,
    };
  });

  // Merge gateways: registries have gatewayAppName
  const mergedGateways = { ...homesNormalized.gateways };
  Object.keys(registriesNormalized.gateways || {}).forEach(gatewayUid => {
    const registryGateway = registriesNormalized.gateways![gatewayUid];
    const homeGateway = mergedGateways[gatewayUid] || {};

    // Merge gateway data, prioritizing registry data
    mergedGateways[gatewayUid] = {
      ...homeGateway,
      ...registryGateway,
      // ✅ DTOs already resolved names, just use them
      name: registryGateway.gatewayAppName || registryGateway.name || homeGateway.name || gatewayUid,
      gatewayAppName: registryGateway.gatewayAppName || homeGateway.gatewayAppName,
    };
  });

  return {
    homes: { ...homesNormalized.homes },
    rooms: { ...homesNormalized.rooms },
    devices: mergedDevices,
    gateways: mergedGateways,
    relationships: {
      deviceToGateway: {
        ...homesNormalized.relationships.deviceToGateway,
        ...(registriesNormalized.relationships?.deviceToGateway || {}),
      },
      deviceToRoom: { ...homesNormalized.relationships.deviceToRoom },
      deviceToHome: { ...homesNormalized.relationships.deviceToHome },
      gatewayToHome: { ...homesNormalized.relationships.gatewayToHome },
      roomToHome: { ...homesNormalized.relationships.roomToHome },
      homeToRooms: { ...homesNormalized.relationships.homeToRooms },
      homeToDevices: { ...homesNormalized.relationships.homeToDevices },
      homeToGateways: { ...homesNormalized.relationships.homeToGateways },
      roomToDevices: { ...homesNormalized.relationships.roomToDevices },
      gatewayToDevices: {
        ...homesNormalized.relationships.gatewayToDevices,
        ...(registriesNormalized.relationships?.gatewayToDevices || {}),
      },
    },
  };
}

// ============================================================================
// Legacy Normalizers (for backward compatibility)
// These work with raw API data instead of DTOs
// TODO: Remove once all code uses DTOs
// ============================================================================

/**
 * @deprecated Use normalizeHomeDTOs instead
 */
export function normalizeHomesData(homesArray: any[]): NormalizedData {
  // Import HomeDTO to convert raw data
  const homeDTOs = HomeDTO.fromApiResponseArray(homesArray);
  return normalizeHomeDTOs(homeDTOs);
}

/**
 * @deprecated Use normalizeRegistryDTOs instead
 */
export function normalizeRegistriesData(registriesArray: any[]): Partial<NormalizedData> {
  // Import GatewayDTO to convert raw data
  const gatewayDTOs = GatewayDTO.fromApiResponseArray(registriesArray);
  return normalizeRegistryDTOs(gatewayDTOs);
}
